import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { setQuestionnaireReviewed } from "@/lib/admin-dashboard.functions";
import { setQuestionnaireArchived } from "@/lib/questionnaire-admin.functions";
import {
  Archive,
  ArrowLeft,
  Download as DownloadIcon,
  FileText,
  FileSpreadsheet,
  Loader2,
  Paperclip,
} from "lucide-react";
import {
  getAdminQuestionnaireDetail,
  getAdminAnswerFileSignedUrl,
  getAdminAnswerFileSignedUrlByPath,
  exportQuestionnaireCsv,
  exportQuestionnairePdf,
  type AdminQuestionnaireDetail,
} from "@/lib/questionnaire-admin.functions";
import { LogoPreview } from "@/components/admin/campaign-design/LogoPreview";
import { parseDesignSnapshot, snapshotSwatches } from "@/lib/campaign-design-answer";
import { attachDesignLogoImage } from "@/lib/campaign-design.functions";
import { rasterizeLogoPng } from "@/lib/campaign-design-raster";
import {
  FIELD_TYPE_LABELS,
  OTHER_VALUE,
  formatEuroAmount,
  matrixRows,
  matrixSteps,
} from "@/lib/questionnaire-logic";

export const Route = createFileRoute("/_admin/admin/questionnaires/$responseId")({
  head: () => ({
    meta: [
      { title: "Fragebogen-Antworten – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: QuestionnaireDetailPage,
});

const dateFmt = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const TYPE_LABELS: Record<string, string> = {
  personaldatenblatt: "Personaldatenblatt",
  wahlkampf: "Wahlkampf",
  ttpa: "TTPA",
  design: "Design",
  design_standard: "Standard-Kampagnendesign",
  test: "Test",
};

function statusLabel(s: string) {
  return s === "completed"
    ? "Abgeschlossen"
    : s === "in_progress"
      ? "In Bearbeitung"
      : "Nicht begonnen";
}

function statusBadgeCls(s: string) {
  if (s === "completed") return "bg-brand-wahlen/15 text-brand-wahlen";
  if (s === "in_progress") return "bg-primary/10 text-primary";
  return "bg-secondary text-muted-foreground";
}

function fieldTypeLabel(t: string) {
  return FIELD_TYPE_LABELS[t] ?? t;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function base64ToBlob(base64: string, mime: string): Blob {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function QuestionnaireDetailPage() {
  const { responseId } = Route.useParams();
  const detailFn = useServerFn(getAdminQuestionnaireDetail);
  const signFn = useServerFn(getAdminAnswerFileSignedUrl);
  const signPathFn = useServerFn(getAdminAnswerFileSignedUrlByPath);
  const csvFn = useServerFn(exportQuestionnaireCsv);
  const pdfFn = useServerFn(exportQuestionnairePdf);
  const reviewFn = useServerFn(setQuestionnaireReviewed);
  const archiveFn = useServerFn(setQuestionnaireArchived);
  const qc = useQueryClient();

  const [busyFile, setBusyFile] = useState<string | null>(null);
  const [busyExport, setBusyExport] = useState<"pdf" | "csv" | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-questionnaire-detail", responseId],
    queryFn: () => detailFn({ data: { responseId, logView: true } }),
    staleTime: 15_000,
  });

  // Ticket AD4a: Nachbearbeitung markieren; steuert den Arbeitsvorrat.
  const reviewMut = useMutation({
    mutationFn: (reviewed: boolean) => reviewFn({ data: { responseId, reviewed } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-questionnaire-detail", responseId] });
      void qc.invalidateQueries({ queryKey: ["admin-questionnaires"] });
      void qc.invalidateQueries({ queryKey: ["admin", "dashboard-stats"] });
      toast.success("Nachbearbeitung gespeichert.");
    },
    onError: (e: Error) => toast.error(e.message || "Speichern fehlgeschlagen."),
  });

  // Internes Archiv: hängt an der Zuweisung, für Kundinnen und Kunden unsichtbar.
  const archiveMut = useMutation({
    mutationFn: (vars: { assignmentId: string; archived: boolean }) =>
      archiveFn({ data: { assignmentIds: [vars.assignmentId], archived: vars.archived } }),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ["admin-questionnaire-detail", responseId] });
      void qc.invalidateQueries({ queryKey: ["admin-questionnaires"] });
      void qc.invalidateQueries({ queryKey: ["admin", "dashboard-stats"] });
      toast.success(vars.archived ? "Eintrag archiviert." : "Eintrag zurückgeholt.");
    },
    onError: (e: Error) => toast.error(e.message || "Speichern fehlgeschlagen."),
  });

  const ansByQ = useMemo(() => {
    const m = new Map<string, AdminQuestionnaireDetail["answers"][number]>();
    for (const a of data?.answers ?? []) m.set(a.questionId, a);
    return m;
  }, [data]);

  async function downloadFile(answerId: string, path?: string) {
    setBusyFile(path ? `${answerId}:${path}` : answerId);
    try {
      const { url } = path
        ? await signPathFn({ data: { answerId, path } })
        : await signFn({ data: { answerId } });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Download fehlgeschlagen.");
    } finally {
      setBusyFile(null);
    }
  }

  async function doExport(kind: "pdf" | "csv") {
    setBusyExport(kind);
    setExportError(null);
    try {
      if (kind === "csv") {
        const res = await csvFn({ data: { responseId } });
        const blob = new Blob([res.content], { type: "text/csv;charset=utf-8" });
        triggerDownload(blob, res.filename);
      } else {
        const res = await pdfFn({ data: { responseId } });
        const blob = base64ToBlob(res.base64, "application/pdf");
        triggerDownload(blob, res.filename);
      }
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Export fehlgeschlagen.");
    } finally {
      setBusyExport(null);
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl">
        <p className="text-sm text-muted-foreground">Lade Antworten…</p>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="mx-auto max-w-4xl">
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : "Antworten konnten nicht geladen werden."}
        </p>
        <Link
          to="/admin/questionnaires"
          className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  const d = data;

  return (
    <div className="mx-auto max-w-4xl">
      <Link
        to="/admin/questionnaires"
        className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Übersicht
      </Link>

      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-primary">{d.template.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {d.client.name}
            {d.client.clientNumber ? ` · ${d.client.clientNumber}` : ""}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <span
            className={
              "rounded px-2 py-0.5 text-xs font-semibold " + statusBadgeCls(d.response.status)
            }
          >
            {statusLabel(d.response.status)}
          </span>
          {d.response.status === "completed" ? (
            <button
              type="button"
              onClick={() => reviewMut.mutate(!d.response.reviewedAt)}
              disabled={reviewMut.isPending}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-primary disabled:opacity-50"
            >
              {d.response.reviewedAt
                ? "Nachbearbeitung zurücknehmen"
                : "Als nachbearbeitet markieren"}
            </button>
          ) : null}
          {d.response.assignmentId ? (
            <button
              type="button"
              onClick={() =>
                archiveMut.mutate({
                  assignmentId: d.response.assignmentId as string,
                  archived: !d.response.archivedAt,
                })
              }
              disabled={archiveMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary disabled:opacity-50"
            >
              <Archive className="h-3.5 w-3.5" aria-hidden />
              {d.response.archivedAt ? "Aus dem Archiv holen" : "Archivieren"}
            </button>
          ) : null}
          {d.response.archivedAt ? (
            <span className="text-[11px] text-muted-foreground">
              Archiviert am {dateFmt.format(new Date(d.response.archivedAt))}
            </span>
          ) : null}
        </div>
      </div>

      <div className="mt-6 grid gap-3 rounded-lg border border-border bg-secondary/40 p-4 text-xs sm:grid-cols-2">
        <MetaRow label="Typ" value={TYPE_LABELS[d.template.type] ?? d.template.type} />
        <MetaRow label="Variante" value={d.template.variant ?? "—"} />
        <MetaRow
          label="Gestartet"
          value={d.response.startedAt ? dateFmt.format(new Date(d.response.startedAt)) : "—"}
        />
        <MetaRow
          label="Abgeschlossen"
          value={d.response.completedAt ? dateFmt.format(new Date(d.response.completedAt)) : "—"}
        />
        <MetaRow label="Letzte Aktivität" value={dateFmt.format(new Date(d.response.updatedAt))} />
        <MetaRow
          label="Nachbearbeitet"
          value={d.response.reviewedAt ? dateFmt.format(new Date(d.response.reviewedAt)) : "—"}
        />
        <MetaRow
          label="Letzter Bearbeiter"
          value={d.response.lastEditorEmail ?? d.response.lastEditorName ?? "—"}
        />
        <MetaRow
          label="Einwilligung (Art. 9 DSGVO)"
          value={
            d.consent
              ? `${dateFmt.format(new Date(d.consent.consentedAt))} · Version ${d.consent.consentVersion}`
              : "—"
          }
          fullWidth
        />
      </div>

      {d.responseConsents.length > 0 && (
        <div className="mt-4 rounded-lg border border-border bg-background p-4">
          <h3 className="font-display text-sm font-extrabold text-primary">
            Fragebogen-Einwilligungen
          </h3>
          <ul className="mt-2 space-y-2 text-xs text-muted-foreground">
            {d.responseConsents.map((c, i) => (
              <li key={i} className="break-words">
                {c.consentText}
                {c.required && <span className="ml-1 text-destructive">*</span>}
                <span className="block text-[11px]">
                  Version {c.consentVersion} · {dateFmt.format(new Date(c.acceptedAt))} · Kanal:{" "}
                  {c.channel}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() => doExport("pdf")}
          disabled={busyExport !== null}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-button px-3 py-1.5 text-xs font-semibold text-brand-button-foreground hover:opacity-90 disabled:opacity-60"
        >
          {busyExport === "pdf" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <FileText className="h-3.5 w-3.5" />
          )}
          Als PDF exportieren
        </button>
        <button
          onClick={() => doExport("csv")}
          disabled={busyExport !== null}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary disabled:opacity-60"
        >
          {busyExport === "csv" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <FileSpreadsheet className="h-3.5 w-3.5" />
          )}
          Als CSV exportieren
        </button>
        {exportError && <span className="text-xs text-destructive">{exportError}</span>}
      </div>

      <div className="mt-8 space-y-6">
        {(() => {
          const sectionById = new Map(d.sections.map((s) => [s.id, s]));
          let lastSection: string | null | undefined = undefined;
          const out: React.ReactNode[] = [];
          for (const q of d.questions) {
            if (!q.visible) continue;
            if (q.sectionId !== lastSection) {
              lastSection = q.sectionId;
              const sec = q.sectionId ? sectionById.get(q.sectionId) : undefined;
              if (sec) {
                out.push(
                  <div key={`sec-${sec.id}`} className="border-b border-border pb-2 pt-2">
                    <h2 className="font-display text-lg font-extrabold text-primary">
                      {sec.title}
                    </h2>
                    {sec.introText && (
                      <p className="mt-1 text-xs text-muted-foreground">{sec.introText}</p>
                    )}
                  </div>,
                );
              }
            }
            const a = ansByQ.get(q.id);
            out.push(
              <div
                key={q.id}
                className="rounded-lg border border-border bg-background p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-sm font-extrabold text-primary">
                      {q.sortOrder + 1}. {q.label}
                      {q.required && <span className="ml-1 text-destructive">*</span>}
                    </h3>
                    {q.helpText && (
                      <p className="mt-1 text-xs text-muted-foreground">{q.helpText}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="whitespace-nowrap rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                      {fieldTypeLabel(q.fieldType)}
                    </span>
                    {q.apiKey && (
                      <code className="whitespace-nowrap rounded bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        {q.apiKey}
                      </code>
                    )}
                  </div>
                </div>
                <div className="mt-3">
                  <AnswerView
                    question={q}
                    answer={a}
                    onDownload={downloadFile}
                    busyFile={busyFile}
                  />
                </div>
              </div>,
            );
          }
          return out;
        })()}
      </div>
    </div>
  );
}

function MetaRow({
  label,
  value,
  fullWidth,
}: {
  label: string;
  value: string;
  fullWidth?: boolean;
}) {
  return (
    <div className={fullWidth ? "sm:col-span-2" : ""}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-0.5 text-sm text-foreground">{value}</p>
    </div>
  );
}

/**
 * Designauswahl: Momentaufnahme des Auswahlzeitpunkts, nicht der heutige Stand
 * der Verwaltung. Fehlt die PNG-Fassung des Logos (Altbestand vor Ticket D3a),
 * wird sie hier einmalig im Hintergrund erzeugt — sonst fehlt das Logo im
 * Export und in der Benachrichtigungsmail.
 */
function DesignAnswerView({
  questionId,
  valueText,
}: {
  questionId: string;
  valueText: string | null;
}) {
  const { responseId } = Route.useParams();
  const snap = parseDesignSnapshot(valueText);
  const attach = useServerFn(attachDesignLogoImage);
  const done = useRef(false);

  useEffect(() => {
    if (!snap || snap.logo.pngPath || !snap.logo.svgMarkup || done.current) return;
    done.current = true;
    let cancelled = false;
    void (async () => {
      const pngBase64 = await rasterizeLogoPng(snap.logo.svgMarkup!, snap.palette);
      if (!pngBase64 || cancelled) return;
      try {
        await attach({ data: { responseId, questionId, pngBase64 } });
      } catch (err) {
        console.error("[campaign-design] Nachtrag der PNG-Fassung fehlgeschlagen", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attach, questionId, responseId, snap]);

  if (!snap) return <p className="text-sm italic text-muted-foreground">— keine Antwort —</p>;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-4">
        {snap.logo.svgMarkup ? (
          <span className="flex h-24 w-40 items-center justify-center rounded-md border border-border bg-background p-2">
            <LogoPreview
              logo={{ name: snap.logo.name, svgMarkup: snap.logo.svgMarkup }}
              palette={{
                id: snap.palette.id,
                name: snap.palette.name,
                description: null,
                colorPrimary: snap.palette.colorPrimary,
                colorSecondary: snap.palette.colorSecondary,
                colorText: snap.palette.colorText,
                sortOrder: 0,
                isActive: true,
              }}
              className="h-full w-full"
            />
          </span>
        ) : null}
        <div className="min-w-0 text-sm">
          <p className="font-semibold text-foreground">Logo: {snap.logo.name}</p>
          <p className="text-foreground">Palette: {snap.palette.name}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Gewählt am {new Date(snap.at).toLocaleDateString("de-DE")}
          </p>
        </div>
      </div>
      <ul className="flex flex-wrap gap-3">
        {snapshotSwatches(snap).map((sw) => (
          <li key={sw.label} className="flex items-center gap-2 text-xs text-muted-foreground">
            <span
              className="h-8 w-8 rounded-md border border-border"
              style={{ backgroundColor: sw.hex }}
              aria-hidden
            />
            {sw.label} {sw.hex}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AnswerView({
  question,
  answer,
  onDownload,
  busyFile,
}: {
  question: AdminQuestionnaireDetail["questions"][number];
  answer: AdminQuestionnaireDetail["answers"][number] | undefined;
  onDownload: (answerId: string, path?: string) => void;
  busyFile: string | null;
}) {
  const cfg = question.config ?? {};
  if (!answer) return <p className="text-sm italic text-muted-foreground">— keine Antwort —</p>;

  switch (question.fieldType as string) {
    case "text_short":
    case "text_long":
    case "email":
      if (!answer.valueText)
        return <p className="text-sm italic text-muted-foreground">— keine Antwort —</p>;
      return <p className="whitespace-pre-wrap text-sm text-foreground">{answer.valueText}</p>;
    case "checkbox":
      return (
        <p className="text-sm font-semibold text-foreground">{answer.valueBool ? "Ja" : "Nein"}</p>
      );
    case "date":
      if (answer.valueUnknown)
        return (
          <p className="text-sm text-foreground">{cfg.unknown_label ?? "noch nicht bekannt"}</p>
        );
      if (!answer.valueDate)
        return <p className="text-sm italic text-muted-foreground">— keine Antwort —</p>;
      return (
        <p className="text-sm text-foreground">
          {new Date(answer.valueDate).toLocaleDateString("de-DE")}
        </p>
      );
    case "scale": {
      if (answer.valueNumber == null)
        return <p className="text-sm italic text-muted-foreground">— keine Antwort —</p>;
      return (
        <div>
          <p className="text-sm font-semibold text-foreground">
            {answer.valueNumber}/5
            {cfg.scale_min_label || cfg.scale_max_label ? (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                (1 = {cfg.scale_min_label ?? "niedrig"}, 5 = {cfg.scale_max_label ?? "hoch"})
              </span>
            ) : null}
          </p>
          {answer.valueText && (
            <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{answer.valueText}</p>
          )}
        </div>
      );
    }
    case "number": {
      if (answer.valueNumber == null)
        return <p className="text-sm italic text-muted-foreground">— keine Antwort —</p>;
      return (
        <p className="text-sm font-semibold text-foreground">
          {formatEuroAmount(answer.valueNumber, cfg.number_unit ?? "€")}
          {cfg.number_estimated ? (
            <span className="ml-2 text-xs font-normal text-muted-foreground">(geschätzt)</span>
          ) : null}
        </p>
      );
    }
    case "matrix": {
      const m = answer.valueMatrix ?? {};
      const rows = matrixRows(cfg).filter((r) => m[r.key] != null);
      const steps = matrixSteps(cfg);
      const max = steps[steps.length - 1] ?? 5;
      if (rows.length === 0)
        return <p className="text-sm italic text-muted-foreground">— keine Antwort —</p>;
      return (
        <ul className="space-y-1 text-sm text-foreground">
          {rows.map((r) => (
            <li key={r.key} className="flex justify-between gap-3 border-b border-border/60 pb-1">
              <span>
                {r.label}
                {(r.left_label || r.right_label) && (
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({r.left_label ?? ""} ↔ {r.right_label ?? ""})
                  </span>
                )}
              </span>
              <span className="font-semibold">
                {m[r.key]}/{max}
              </span>
            </li>
          ))}
        </ul>
      );
    }
    case "image_priority": {
      const ranked = answer.valueSelected ?? [];
      if (ranked.length === 0)
        return <p className="text-sm italic text-muted-foreground">— keine Antwort —</p>;
      return (
        <ol className="space-y-1 text-sm text-foreground">
          {ranked.map((v, i) => (
            <li key={v} className="border-b border-border/60 pb-1">
              <span className="font-semibold">{i + 1}.</span>{" "}
              {question.options?.find((o) => o.value === v)?.label ?? v}
            </li>
          ))}
        </ol>
      );
    }
    case "image_rating": {
      const panels = answer.valuePanels ?? {};
      const rated = (question.options ?? []).filter(
        (o) => panels[o.value]?.value != null || panels[o.value]?.comment,
      );
      if (rated.length === 0)
        return <p className="text-sm italic text-muted-foreground">— keine Antwort —</p>;
      return (
        <ul className="space-y-1 text-sm text-foreground">
          {rated.map((o) => {
            const p = panels[o.value] ?? {};
            return (
              <li key={o.value} className="border-b border-border/60 pb-1">
                <div className="flex justify-between gap-3">
                  <span>{o.label}</span>
                  <span className="font-semibold">{p.value == null ? "—" : `${p.value}/5`}</span>
                </div>
                {p.comment && (
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                    {p.comment}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      );
    }
    // Ticket D3: Kombination aus Palette und Logo. Angezeigt wird die
    // Momentaufnahme des Auswahlzeitpunkts, nicht der heutige Stand der
    // Verwaltung.
    case "campaign_design":
      return <DesignAnswerView questionId={question.id} valueText={answer.valueText} />;
    case "radio":
    case "select": {
      const sel = answer.valueSelected ?? [];
      if (sel.length === 0)
        return <p className="text-sm italic text-muted-foreground">— keine Antwort —</p>;
      const labels = sel.map((v) =>
        v === OTHER_VALUE
          ? `${cfg.other_label ?? "Sonstiges"}: ${answer.valueOtherText ?? ""}`.trim()
          : (question.options?.find((o) => o.value === v)?.label ?? v),
      );
      return (
        <div className="flex flex-wrap gap-1.5">
          {labels.map((l, i) => (
            <span
              key={i}
              className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
            >
              {l}
            </span>
          ))}
        </div>
      );
    }

    case "file": {
      const list = Array.isArray(answer.valueFiles) ? answer.valueFiles : [];
      const entries =
        list.length > 0
          ? list.map((f) => ({
              key: f.path,
              label: f.name ?? f.path,
              path: f.path as string | undefined,
            }))
          : answer.filePath
            ? [
                {
                  key: answer.filePath,
                  label: answer.filePath.split("/").pop() ?? "Datei",
                  path: undefined as string | undefined,
                },
              ]
            : [];
      if (entries.length === 0)
        return <p className="text-sm italic text-muted-foreground">— keine Datei —</p>;
      return (
        <ul className="flex flex-wrap gap-1.5">
          {entries.map((e) => {
            const busyKey = e.path ? `${answer.id}:${e.path}` : answer.id;
            return (
              <li key={e.key}>
                <button
                  onClick={() => onDownload(answer.id, e.path)}
                  disabled={busyFile === busyKey}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-semibold text-foreground hover:bg-secondary disabled:opacity-60"
                >
                  {busyFile === busyKey ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Paperclip className="h-3.5 w-3.5" />
                  )}
                  {e.label}
                  <DownloadIcon className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      );
    }
    default:
      return <p className="text-sm italic text-muted-foreground">— keine Antwort —</p>;
  }
}
